/* Nota Tanıma: name what you hear, find what you name, intervals. */
import { useEffect, useRef, useState } from 'react';
import { audio, type NoteEvent } from '../../core/audio';
import { trackNotes } from '../../core/mic';
import { getSettings } from '../../core/store';
import {
  intervalName,
  keyFlat,
  keyName,
  mod,
  NATURALS,
  noteName,
  noteParts,
  octave,
  openString,
  pcName,
  positions,
  stringLabel,
} from '../../core/theory';
import { choice, type Rng, randInt } from '../../core/util';
import { Fretboard } from '../../ui/Fretboard';
import { emptySlots, Slots, type SlotView } from '../../ui/widgets';
import type {
  ChoiceQuestion,
  CustomQuestion,
  ExerciseConfig,
  InputKind,
  NotesQuestion,
  Question,
  QuestionApi,
} from '../exercise/types';
import type { ChallengeDef, LevelDef } from '../levels';

const DEG_CHAR: Record<number, (t: number) => string> = {
  0: () => '1. derece (tonik): durgun, "ev" hissi verir.',
  2: (t) => `2. derece: hareketlidir, ${pcName(t)} notasına inmek ister.`,
  4: () => '3. derece: parlak ve sakin; majör rengini verir.',
  5: (t) => `4. derece: ${pcName((t + 4) % 12)} notasına inmek ister.`,
  7: () => '5. derece: güçlü ve sağlam duyulur.',
  9: () => '6. derece: yumuşak, biraz hüzünlü.',
  11: (t) => `7. derece: gerilimlidir, ${pcName(t)} notasına çıkmak ister.`,
};
function degreeText(iv: number, tonic: number): string {
  const f = DEG_CHAR[iv];
  if (f) return f(tonic);
  const names: Record<number, string> = { 1: '♭2', 3: '♭3', 6: '♯4 / ♭5', 8: '♭6', 10: '♭7' };
  return `Gam dışı bir nota (${names[iv] || ''}): en yakın gam notasına çözülmek ister.`;
}

// ---------------------------------------------------------------- "Hangi nota?"
const HANGI_SETS: Record<number, number[]> = {
  1: [0, 4, 7],
  2: [0, 2, 4, 5, 7],
  3: [0, 2, 4, 5, 7, 9, 11],
  4: [0, 2, 4, 5, 7, 9, 11],
  5: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};

export function hangiQ(level: number, rng: Rng, o: { maxMiss?: number; noCadence?: boolean } = {}): ChoiceQuestion {
  const inst = getSettings().instrument;
  if (level === 6) {
    const naturals = [57, 59, 60, 62, 64, 65, 67, 69];
    const ref = choice(naturals, rng);
    let d = 0;
    let tries = 0;
    while (d === 0 && tries++ < 20) d = randInt(-11, 11, rng);
    if (d === 0) d = 4;
    const target = ref + d;
    const opts = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((pc) => ({ id: pc, label: pcName(pc, { flat: false }) }));
    const dir = d > 0 ? ' yukarı' : ' aşağı';
    return {
      type: 'choice',
      prompt: `İlk nota **${noteName(ref)}**. İkinci nota hangisi?`,
      info: 'Referans notadan aralığı düşün',
      options: opts,
      answer: mod(target, 12),
      maxMiss: o.maxMiss,
      play: () => audio.playNotes([ref, target], { bpm: 70, inst }),
      preview: (pc) => audio.playNotes([ref, ref + mod(Number(pc) - mod(ref, 12), 12) - (d < 0 ? 12 : 0)], { bpm: 70 }),
      answerText: () => `${noteName(target)} (${intervalName(Math.abs(d))}${dir})`,
      explain: () => `İki nota arası: **${intervalName(Math.abs(d))}${dir}**.`,
      reviewLabel: `${noteName(ref)} sonrası`,
      columns: null,
    };
  }
  let tonic = 0;
  if (level === 4) tonic = choice([7, 2, 5, 9, 10, 4], rng);
  const flat = keyFlat(tonic, 'major');
  const ivs = HANGI_SETS[level] as number[];
  const iv = choice(ivs, rng);
  const tonicMidi = tonic <= 5 ? 60 + tonic : 48 + tonic;
  const target = tonicMidi + iv;
  const voice = inst === 'sine' ? 'sine' : 'piano';
  const cad = () => audio.cadenceEvents(tonic, 'major', { bpm: 132 });
  const noteEv = (t0: number, m: number): NoteEvent => ({
    kind: 'note',
    midi: m,
    t: t0,
    dur: 1.4,
    inst: voice,
    vel: 0.8,
  });
  const playOnly = () => audio.play([noteEv(0, target)], { length: 1.5 });
  const playFull = () => {
    const c = cad();
    return audio.play(c.ev.concat([noteEv(c.length + 0.3, target)]), { length: c.length + 1.8 });
  };
  return {
    type: 'choice',
    prompt: 'Tonaliteden sonra çalan nota hangisi?',
    info: `${keyName(tonic, 'major')} · önce I–IV–V–I kadansı, sonra nota`,
    options: ivs.map((i) => ({ id: mod(tonic + i, 12), label: pcName(mod(tonic + i, 12), { flat }) })),
    answer: mod(target, 12),
    maxMiss: o.maxMiss,
    play: o.noCadence ? playOnly : playFull,
    extraTools: [
      {
        label: 'Kadans',
        icon: 'loop',
        run: () => {
          const c = cad();
          return audio.play(c.ev, { length: c.length });
        },
      },
      { label: 'Sadece nota', icon: 'play', run: () => playOnly() },
    ],
    preview: (pc) => audio.play([noteEv(0, tonicMidi + mod(Number(pc) - tonic, 12))], { length: 1.5 }),
    answerText: () => pcName(target % 12, { flat }),
    explain: (ok) => `${ok ? '' : `Doğrusu **${pcName(target % 12, { flat })}**. `}${degreeText(iv, tonic)}`,
    reviewLabel: `${keyName(tonic, 'major')} içinde nota`,
  };
}

// ---------------------------------------------------------------- "Sapta bul"
const SAP_STRINGS: Record<number, number[]> = { 1: [6], 2: [5], 3: [4, 3], 4: [2, 1], 5: [6, 5, 4, 3, 2, 1] };

export function sapQ(level: number, rng: Rng, o: { maxMiss?: number } = {}): NotesQuestion {
  const sn = choice(SAP_STRINGS[level] as number[], rng);
  const open = openString(sn);
  const frets: number[] = [];
  for (let f = 0; f <= (level === 5 ? 11 : 12); f++)
    if (level === 5 || NATURALS.includes(mod(open + f, 12))) frets.push(f);
  const f = choice(frets, rng);
  const midi = open + f;
  const flat = level === 5 && rng() < 0.5 && [1, 3, 6, 8, 10].includes(mod(midi, 12));
  // the prompt names the note, not the octave: the open string and its 12th fret are both right, also on
  // level 5 (and its challenge), which only asks frets 0–11 but shows the board up to the 12th
  const places = Array.from({ length: 13 }, (_, x) => x).filter((x) => mod(x - f, 12) === 0);
  return {
    type: 'notes',
    target: [midi],
    flex: false,
    fb: { from: 0, to: 12 },
    maxMiss: o.maxMiss,
    flat,
    prompt: `**${sn}. tel**de **${pcName(mod(midi, 12), { flat })}** notasını çal`,
    info: stringLabel(sn),
    screenHint: 'Notayı o telde bul ve üzerine tıkla.',
    match: (m, meta) => (meta ? meta.s === sn && places.includes(meta.f) : places.some((x) => open + x === m)),
    revealMarks: places.map((x) => ({ s: sn, f: x })),
    answerText: () =>
      `${sn}. tel, ${places.join('. ya da ')}. perde (${places.map((x) => noteName(open + x, { flat })).join(' / ')})`,
    reviewLabel: `${sn}. telde ${pcName(mod(midi, 12), { flat })}`,
    hints: [
      {
        label: 'Bölge',
        run: () => `Bu notayı ${sn}. telde ${f <= 4 ? '0–4.' : f <= 8 ? '5–8.' : '9–12.'} perdeler arasında ara.`,
      },
      {
        label: 'Sesini duy',
        run: () => {
          audio.playNote(midi, { inst: 'guitar', dur: 1.2 });
          return 'Notanın sesi bu. Aynı sesi o telde bul.';
        },
      },
      { label: 'Göster', run: (api) => api.reveal() },
    ],
  };
}

// ---------------------------------------------------------------- octave hunt
function OctaveHunt({ api, pc }: { api: QuestionApi; pc: number }) {
  const [slots, setSlots] = useState<SlotView[]>(() => emptySlots(3));
  const [live, setLive] = useState(api.input === 'mic' ? 'Dinliyorum…' : 'Sap üzerinde tıkla.');
  const found = useRef(new Set<number>());
  const take = (m: number) => {
    if (api.isDone()) return;
    if (mod(m, 12) !== pc) {
      api.feedback(`Bu **${noteName(m)}**. Aradığın ${pcName(pc)}.`, 'bad');
      api.miss();
      return;
    }
    const oc = octave(m);
    if (found.current.has(oc)) {
      api.feedback(`Bu oktavı zaten buldun (${noteName(m)}). Başka bir yükseklik dene.`, 'info');
      return;
    }
    found.current.add(oc);
    const p = noteParts(m);
    const i = found.current.size - 1;
    setSlots((s) => s.map((x, k) => (k === i ? { ...x, main: p.main, sub: p.sub, cls: 'ok' } : x)));
    api.feedback(null);
    if (found.current.size >= 3) api.done(true);
  };
  const takeRef = useRef(take);
  takeRef.current = take;
  useEffect(() => {
    if (api.input !== 'mic') return;
    return trackNotes('guitar', (ev) => {
      setLive(`Duyulan: ${noteName(ev.midi)}`);
      takeRef.current(ev.midi);
    });
  }, [api]);
  return (
    <>
      <Slots slots={slots} current={found.current.size} />
      <div className="live-note">{live}</div>
      {api.input !== 'mic' ? (
        <Fretboard
          from={0}
          to={15}
          onPick={(_s, _f, m) => {
            audio.playNote(m, { inst: 'guitar', dur: 0.7 });
            take(m);
          }}
        />
      ) : null}
    </>
  );
}

function octaveQ(rng: Rng): CustomQuestion {
  const pc = choice([0, 2, 4, 5, 7, 9, 11], rng);
  const low = 40 + mod(pc - 4, 12);
  return {
    type: 'custom',
    prompt: `**${pcName(pc)}** notasını üç farklı oktavda çal`,
    info: 'Sapın farklı bölgelerini kullan',
    reviewLabel: `${pcName(pc)} oktav avı`,
    answerText: () =>
      positions(low, 12).length ? `Örnek: ${[low, low + 12, low + 24].map((m) => noteName(m)).join(', ')}` : '',
    render: (api) => <OctaveHunt api={api} pc={pc} />,
  };
}

// ---------------------------------------------------------------- intervals
const IV_SETS: Record<number, number[]> = {
  1: [4, 7, 12],
  2: [3, 4, 5, 7],
  3: [1, 2, 3, 4, 5, 7, 12],
  4: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  5: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  6: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
};

export function aralikQ(level: number, rng: Rng, o: { maxMiss?: number } = {}): ChoiceQuestion {
  const ivs = IV_SETS[level] as number[];
  const iv = choice(ivs, rng);
  const desc = level === 5;
  const harm = level === 6;
  const root = desc ? randInt(62, 74, rng) : randInt(50, 64, rng);
  const b = root + (desc ? -iv : iv);
  const inst = getSettings().instrument;
  const playIv = (x: number) =>
    harm
      ? audio.playHarm([root, root + x], { inst, dur: 1.8 })
      : audio.playNotes([root, root + (desc ? -x : x)], { bpm: 76, inst });
  return {
    type: 'choice',
    prompt: harm
      ? 'Birlikte çalan iki nota arasındaki aralık ne?'
      : `${desc ? 'İnen' : 'Çıkan'} iki nota arasındaki aralık ne?`,
    info: harm ? 'Harmonik aralık' : desc ? 'Melodik · aşağı' : 'Melodik · yukarı',
    options: ivs.map((i) => ({ id: i, label: intervalName(i), sub: `${i} yarım ses` })),
    answer: iv,
    maxMiss: o.maxMiss,
    play: () => playIv(iv),
    preview: (x) => playIv(Number(x)),
    answerText: () => `${intervalName(iv)} (${iv} yarım ses)`,
    explain: (ok) =>
      `${ok ? '' : `Doğrusu **${intervalName(iv)}**. `}${noteName(root)} → ${noteName(b)}: ${iv} yarım ses, aynı telde ${iv} perde.`,
    reviewLabel: `Aralık: ${noteName(root)}${harm ? ' + ' : desc ? ' ↓ ' : ' ↑ '}${noteName(b)}`,
  };
}

// ---------------------------------------------------------------- definitions
interface NotaGroup {
  id: string;
  title: string;
  sub: string;
  input: InputKind;
  total: number;
  rules: string[];
  levels: NotaLevel[];
  make: (lv: NotaLevel, rng: Rng) => Question;
}
export interface NotaLevel extends LevelDef {
  group: NotaGroup;
}

function group(g: Omit<NotaGroup, 'levels'> & { levels: { n: number; title: string; desc: string }[] }): NotaGroup {
  const out = g as unknown as NotaGroup;
  out.levels = g.levels.map((l) => ({
    ...l,
    group: out,
    key: `nota.egz.${g.id}.${l.n}`,
    route: `nota.egz.${g.id}.${l.n}`,
  }));
  return out;
}

export const EX_GROUPS: NotaGroup[] = [
  group({
    id: 'hangi',
    title: 'Hangi nota?',
    sub: 'Önce kadans tonaliteyi kurar, sonra çalınan notanın adını bulursun. Bu, "fonksiyonel kulak" yöntemidir.',
    input: 'none',
    total: 10,
    rules: [
      'Kadansı dikkatle dinle: tonik "ev" gibi duyulur.',
      'Notayı duyunca içinden toniğe doğru söyle; kaç adımda vardığını say.',
    ],
    levels: [
      { n: 1, title: 'Üç nota', desc: '{{pc:C}} majörde {{pc:C}}, {{pc:E}} ve {{pc:G}}.' },
      { n: 2, title: 'Beş nota', desc: "{{pc:C}}'den {{pc:G}}'ye ilk beş derece." },
      { n: 3, title: 'Bütün gam', desc: '{{pc:C}} majörün yedi notası.' },
      {
        n: 4,
        title: 'Farklı tonalite',
        desc: '{{pc:G}}, {{pc:D}}, {{pc:F}}, {{pc:A}}, {{pc:Bb}} ve {{pc:E}} majörde yedi nota.',
      },
      { n: 5, title: 'Kromatik', desc: 'On iki notanın hepsi, {{pc:C}} majör bağlamında.' },
      { n: 6, title: 'Referans nota', desc: 'Kadans yok: adı verilen ilk notadan ikinciyi bul.' },
    ],
    make: (lv, rng) => hangiQ(lv.n, rng),
  }),
  group({
    id: 'sap',
    title: 'Sapta bul',
    sub: 'Adı verilen notayı istenen telde çal. Mikrofon kapalıysa sapa tıklayarak cevap ver.',
    input: 'notes',
    total: 10,
    rules: [
      'Uygulama bir tel ve nota söyler, sen o perdeyi bulup çalarsın.',
      'Mikrofon tel ayırt edemez; doğru yükseklikteki nota kabul edilir. Ekrandan girişte tel de kontrol edilir.',
    ],
    levels: [
      {
        n: 1,
        title: '6. tel',
        desc: 'Kalın Mi telindeki doğal notalar (0–12. perde). Barre akorların kökleri burada.',
      },
      { n: 2, title: '5. tel', desc: 'La telindeki doğal notalar.' },
      { n: 3, title: '4. ve 3. tel', desc: 'Re ve Sol tellerindeki doğal notalar.' },
      { n: 4, title: '2. ve 1. tel', desc: 'Si ve ince Mi tellerindeki doğal notalar.' },
      { n: 5, title: 'Bütün teller', desc: 'Diyez ve bemoller dahil, bütün tellerde 0–11. perde.' },
      { n: 6, title: 'Oktav avı', desc: 'Aynı notayı üç farklı yükseklikte bul.' },
    ],
    make: (lv, rng) => (lv.n === 6 ? octaveQ(rng) : sapQ(lv.n, rng)),
  }),
  group({
    id: 'aralik',
    title: 'Aralık tanıma',
    sub: 'İki nota arasındaki mesafeyi adıyla tanı. Akor ve melodi çıkarmanın temeli.',
    input: 'none',
    total: 12,
    rules: [
      'Aralığı içinden şarkılarla eşleştir: dersteki tablodaki hafıza ipuçlarına bakabilirsin.',
      'Yanlış seçimden sonra "Karşılaştır" ile iki aralığı arka arkaya dinle.',
    ],
    levels: [
      { n: 1, title: 'Üç temel aralık', desc: "Büyük 3'lü, tam 5'li ve oktav (yukarı)." },
      { n: 2, title: "3'lü, 4'lü, 5'li", desc: "Küçük ve büyük 3'lü, tam 4'lü, tam 5'li." },
      { n: 3, title: 'Yaygın aralıklar', desc: "2'liler ve 3'lüler, tam 4'lü, tam 5'li, oktav." },
      { n: 4, title: 'Bütün aralıklar', desc: 'Oktava kadar bütün çıkan aralıklar.' },
      { n: 5, title: 'İnen aralıklar', desc: 'Aynı aralıklar, bu kez aşağı doğru.' },
      { n: 6, title: 'Harmonik', desc: 'İki nota aynı anda çalınır.' },
    ],
    make: (lv, rng) => aralikQ(lv.n, rng),
  }),
];

export const LEVELS: NotaLevel[] = EX_GROUPS.flatMap((g) => g.levels);

export const CHALLENGES: ChallengeDef[] = [
  {
    id: 'maraton',
    title: 'Nota maratonu',
    desc: '60 saniyede {{pc:C}} majörün notalarını tanı. Kadans yalnızca başta çalar.',
    badge: '60 sn',
    input: 'none',
    key: 'nota.meydan.maraton',
    route: 'nota.meydan.maraton',
    cfg: {
      timeLimit: 60,
      autoAdvance: true,
      starAt: [8, 14, 20],
      rules: [
        'İlk soruda kadansı dinle, sonra yalnızca notalar çalar.',
        '"Kadans" düğmesiyle tonaliteyi istediğin an tekrar duyabilirsin.',
      ],
      make: (i, _st, rng) => hangiQ(3, rng, { maxMiss: 1, noCadence: i > 0 }),
    },
  },
  {
    id: 'sap',
    title: 'Sap ustası',
    desc: '60 saniyede bütün tellerde istenen notaları bul.',
    badge: '60 sn',
    input: 'notes',
    key: 'nota.meydan.sap',
    route: 'nota.meydan.sap',
    cfg: {
      timeLimit: 60,
      autoAdvance: true,
      starAt: [6, 11, 16],
      make: (_i, _st, rng) => sapQ(5, rng, { maxMiss: 2 }),
    },
  },
  {
    id: 'aralik',
    title: 'Aralık ustası',
    desc: 'Yirmi aralık, tek deneme hakkı. Ne kadar isabetlisin?',
    badge: '20 soru',
    input: 'none',
    key: 'nota.meydan.aralik',
    route: 'nota.meydan.aralik',
    cfg: {
      total: 20,
      autoAdvance: true,
      starAt: [10, 15, 18],
      make: (_i, _st, rng) => aralikQ(4, rng, { maxMiss: 1 }),
    },
  },
];

export function levelConfig(level: NotaLevel): ExerciseConfig {
  const g = level.group;
  const next = g.levels[g.levels.indexOf(level) + 1];
  return {
    key: level.key,
    module: 'nota',
    back: 'nota.antrenman',
    title: `${g.title}\u00a0· ${level.title}`, // the dot stays with the group name when the title wraps
    levelNo: level.n,
    desc: level.desc,
    total: g.total,
    input: g.input,
    rules: g.rules,
    next: next ? next.route : null,
    make: () => g.make(level, Math.random),
  };
}
